// Command palette (Cmd+K / Ctrl+K). Fuzzy search across:
//   - module nav items (with keyboard accelerators shown when bound)
//   - recent notifications (when provided by the caller)
//   - family members (when provided by the caller)
//
// The palette is rendered as a centered dialog on every viewport —
// the "Cmd+K" UX is viewport-independent, and on mobile the top bar
// (when present at lg+) still offers the same trigger. On mobile
// (< sm) the dialog fills more of the screen.
//
// Keyboard:
//   - Up / Down: move selection
//   - Enter:    invoke the selected item
//   - Escape:   close (handled by the parent)
//   - / :       focus the search input (handled internally)

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AnimatePresence, motion } from "motion/react";
import { Command, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { modKeyDisplay } from "@/lib/hooks/useGlobalShortcuts";

export type CommandPaletteItem = {
  id: string;
  /** Section heading (optional). Items in the same section are
   *  grouped visually but searched as one flat list. */
  section?: string;
  /** Searchable label + sublabel. */
  label: string;
  hint?: string;
  /** Rendered as the leading icon. Optional. */
  icon?: ReactNode;
  /** Searchable keywords (in addition to label). */
  keywords?: string[];
  /** Disabled items are rendered but not selectable. */
  disabled?: boolean;
  onSelect: () => void;
};

export function CommandPalette({
  open,
  onClose,
  items,
  placeholder = "搜尋模組、家人、動態…",
  emptyText = "找不到符合的結果",
}: {
  open: boolean;
  onClose: () => void;
  items: CommandPaletteItem[];
  placeholder?: string;
  emptyText?: string;
}) {
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Reset state every time the palette opens; otherwise typing into
  // a previous query would be visible when the user re-opens.
  useEffect(() => {
    if (open) {
      setQuery("");
      setHighlight(0);
      // Focus the input on the next tick so the dialog/animation
      // have settled.
      const t = window.setTimeout(() => inputRef.current?.focus(), 30);
      return () => window.clearTimeout(t);
    }
  }, [open]);

  // Filter + group results. The filter is intentionally simple:
  // substring match on label / hint / keywords, case-insensitive.
  // A real fuzzy search would be nice, but the result set here is
  // small enough (≤ 50 items) that substring search is fine.
  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.trim().toLowerCase();
    return items.filter((it) => {
      if (it.label.toLowerCase().includes(q)) return true;
      if (it.hint?.toLowerCase().includes(q)) return true;
      if (it.keywords?.some((k) => k.toLowerCase().includes(q))) return true;
      return false;
    });
  }, [items, query]);

  // Group by section for visual hierarchy. Sections stay in the
  // order they were first defined in `items`.
  const grouped = useMemo(() => {
    const order: string[] = [];
    const bySection: Record<string, CommandPaletteItem[]> = {};
    for (const item of filtered) {
      const sec = item.section ?? "";
      if (!bySection[sec]) {
        order.push(sec);
        bySection[sec] = [];
      }
      bySection[sec].push(item);
    }
    return order.map((sec) => ({ section: sec, items: bySection[sec] ?? [] }));
  }, [filtered]);

  const flat = useMemo(() => grouped.flatMap((g) => g.items), [grouped]);

  // Clamp highlight when filter changes so it doesn't point past
  // the end of the new list.
  useEffect(() => {
    setHighlight((h) => Math.min(h, Math.max(0, flat.length - 1)));
  }, [flat.length]);

  useEffect(() => {
    const root = listRef.current;
    if (!root) return;
    const el = root.querySelector<HTMLElement>(`[data-idx="${highlight}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [highlight]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlight((h) => Math.min(flat.length - 1, h + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlight((h) => Math.max(0, h - 1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const item = flat[highlight];
        if (item && !item.disabled) {
          item.onSelect();
          onClose();
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    },
    [flat, highlight, onClose],
  );

  return (
    <AnimatePresence>
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="指令面板"
          className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-[10vh] sm:pt-[15vh]"
          onKeyDown={onKeyDown}
        >
          <motion.button
            type="button"
            aria-label="關閉指令面板"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, y: -12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.98 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 flex w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-border/60 bg-background shadow-2xl"
          >
            <div className="flex items-center gap-3 border-b border-border/60 px-4">
              <Search className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={placeholder}
                aria-label="搜尋"
                className="h-14 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground"
                autoComplete="off"
                spellCheck={false}
              />
              <kbd className="hidden items-center gap-1 rounded-md border border-border/60 bg-secondary/50 px-2 py-1 text-[10px] font-mono text-muted-foreground sm:inline-flex">
                <Command className="h-3 w-3" aria-hidden /> K
              </kbd>
            </div>
            <div
              ref={listRef}
              className="max-h-[60vh] overflow-y-auto overscroll-contain py-1"
            >
              {flat.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                  {emptyText}
                </p>
              ) : (
                grouped.map((group) => (
                  <div key={group.section || "default"} className="px-1">
                    {group.section && (
                      <div className="px-3 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {group.section}
                      </div>
                    )}
                    {group.items.map((it) => {
                      // The flat index is the absolute position; we
                      // need it for keyboard navigation.
                      const idx = flat.indexOf(it);
                      const active = idx === highlight;
                      return (
                        <button
                          key={it.id}
                          data-idx={idx}
                          type="button"
                          disabled={it.disabled}
                          onMouseEnter={() => setHighlight(idx)}
                          onClick={() => {
                            if (!it.disabled) {
                              it.onSelect();
                              onClose();
                            }
                          }}
                          className={cn(
                            "flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm transition-colors",
                            active ? "bg-primary/10 text-foreground" : "text-foreground/85",
                            it.disabled && "cursor-not-allowed opacity-50",
                          )}
                        >
                          {it.icon ? (
                            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-secondary/60 text-foreground/80">
                              {it.icon}
                            </span>
                          ) : (
                            <span className="w-2 shrink-0" />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-medium">{it.label}</div>
                            {it.hint && (
                              <div className="truncate text-xs text-muted-foreground">
                                {it.hint}
                              </div>
                            )}
                          </div>
                          {active && (
                            <span className="ml-auto text-[10px] text-muted-foreground">↵</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
            <div className="flex items-center gap-3 border-t border-border/60 bg-secondary/30 px-4 py-2 text-[10px] text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <kbd className="rounded border border-border/60 bg-background px-1.5 py-0.5 font-mono">↑</kbd>
                <kbd className="rounded border border-border/60 bg-background px-1.5 py-0.5 font-mono">↓</kbd>
                切換
              </span>
              <span className="inline-flex items-center gap-1">
                <kbd className="rounded border border-border/60 bg-background px-1.5 py-0.5 font-mono">↵</kbd>
                開啟
              </span>
              <span className="inline-flex items-center gap-1">
                <kbd className="rounded border border-border/60 bg-background px-1.5 py-0.5 font-mono">esc</kbd>
                關閉
              </span>
              <span className="ml-auto hidden items-center gap-1 sm:inline-flex">
                <kbd className="rounded border border-border/60 bg-background px-1.5 py-0.5 font-mono">{modKeyDisplay()}</kbd>
                <kbd className="rounded border border-border/60 bg-background px-1.5 py-0.5 font-mono">K</kbd>
                開啟指令面板
              </span>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
